import { useState, useCallback, useEffect, useRef } from "react";
import { apiClient } from "../service/tekkr-service";
import { openGoogleDrivePicker } from "../../lib/google-picker";
import { FileUpload } from "../../components/shared/chat/chat-input-box";
import { useUploadFileMutation } from "../mutations/upload-file";
import { openPopupAndWaitForClose } from "../../lib/open-popup";

type GoogleDriveStatus = "idle" | "connecting" | "picking" | "importing" | "syncing";

export interface SyncProgress {
    status: string;
    docsFound: number;
    docsIndexed: number;
    docsFailed: number;
    progress: number | null;
}

export function useGoogleDrive() {
    const [status, setStatus] = useState<GoogleDriveStatus>("idle");
    const [syncJobId, setSyncJobId] = useState<string | null>(null);
    const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
    const uploadFileMutation = useUploadFileMutation();
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Poll sync job progress
    useEffect(() => {
        if (!syncJobId) return;

        const poll = async () => {
            try {
                const res = await apiClient.getGoogleDriveSyncStatus({ params: { jobId: syncJobId } });
                if (res.status === 200) {
                    setSyncProgress(res.body);
                    if (res.body.status === "completed" || res.body.status === "failed") {
                        setSyncJobId(null);
                        setStatus("idle");
                    }
                }
            } catch {
                // ignore polling errors
            }
        };

        poll();
        pollIntervalRef.current = setInterval(poll, 3000);

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [syncJobId]);

    const openDrive = useCallback(
        async (onFilesReady: (files: FileUpload[]) => void) => {
            try {
                let accessToken: string | undefined;
                let pickerApiKey: string | undefined;

                try {
                    const tokenRes = await apiClient.getGoogleDriveAccessToken();
                    if (tokenRes.status === 200) {
                        accessToken = tokenRes.body.accessToken;
                        pickerApiKey = tokenRes.body.pickerApiKey;
                    }
                } catch {
                    // not connected yet, will initiate connect flow below
                }

                if (!accessToken) {
                    setStatus("connecting");

                    const connectRes = await apiClient.createGoogleDriveConnectSession({
                        body: {},
                    });
                    if (connectRes.status !== 200) {
                        throw new Error("Failed to create connect session");
                    }

                    await openPopupAndWaitForClose(connectRes.body.connectLink);

                    // After OAuth, set up source config and trigger sync
                    try {
                        const setupRes = await apiClient.setupGoogleDrive({ body: {} });
                        if (setupRes.status === 200) {
                            setSyncJobId(setupRes.body.jobId);
                            setStatus("syncing");
                        }
                    } catch {
                        // setup failed, continue with picker flow
                    }

                    const tokenRes = await apiClient.getGoogleDriveAccessToken();
                    if (tokenRes.status !== 200) {
                        throw new Error("Failed to get access token after connecting");
                    }
                    accessToken = tokenRes.body.accessToken;
                    pickerApiKey = tokenRes.body.pickerApiKey;
                }

                setStatus("picking");
                const selectedFiles = await openGoogleDrivePicker({
                    accessToken,
                    developerKey: pickerApiKey || undefined,
                });

                if (selectedFiles.length === 0) {
                    setStatus(syncJobId ? "syncing" : "idle");
                    return;
                }

                setStatus("importing");

                const contentsRes = await apiClient.importGoogleDriveFiles({
                    body: {
                        files: selectedFiles.map((f) => ({
                            fileId: f.id,
                            mimeType: f.mimeType,
                            name: f.name,
                        })),
                    },
                });

                if (contentsRes.status !== 200) {
                    throw new Error("Failed to fetch file contents");
                }

                const uploadedFiles: FileUpload[] = [];
                for (const fileContent of contentsRes.body.files) {
                    if (fileContent.error) continue;

                    const blob = new Blob([fileContent.content], { type: "text/plain" });
                    const file = new File([blob], fileContent.name ?? "google-drive-file.txt", {
                        type: "text/plain",
                    });

                    const result = await uploadFileMutation.mutateAsync({ file });
                    uploadedFiles.push({
                        id: result.id,
                        name: fileContent.name ?? "google-drive-file.txt",
                        uploading: false,
                        mimeType: "text/plain",
                        source: "google-drive",
                    });
                }

                onFilesReady(uploadedFiles);
                setStatus(syncJobId ? "syncing" : "idle");
            } finally {
                if (status !== "syncing") {
                    setStatus("idle");
                }
            }
        },
        [uploadFileMutation, syncJobId, status]
    );

    return {
        status,
        openDrive,
        isLoading: status !== "idle" && status !== "syncing",
        syncProgress,
        isSyncing: status === "syncing",
    };
}
